import React, { useEffect, useState } from 'react';
import { Eye, Users, CalendarDays, TrendingUp } from 'lucide-react';
import { api } from '../auth/AuthContext';

const PAGE_NAMES = {
  '/': 'Home', '/about': 'About', '/programs': 'Programs', '/faculty': 'Faculty',
  '/campus': 'Campus', '/admissions': 'Admissions', '/resources': 'Resources',
};

export const VisitorsPanel = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get('/analytics/summary').then((r) => setData(r.data)).catch(() => setError(true));
  }, []);

  if (error) return <div className="text-center text-stone-500 py-12" data-testid="visitors-error">Failed to load visitor stats.</div>;
  if (!data) return <div className="text-center text-stone-500 py-12" data-testid="visitors-loading">Loading stats...</div>;

  const cards = [
    { label: 'Total Visits', value: data.total.visits, sub: `${data.total.unique} unique visitors`, icon: Eye },
    { label: 'Today', value: data.today.visits, sub: `${data.today.unique} unique visitors`, icon: CalendarDays },
    { label: 'Last 7 Days', value: data.last7.visits, sub: `${data.last7.unique} unique visitors`, icon: TrendingUp },
    { label: 'Last 30 Days', value: data.last30.visits, sub: `${data.last30.unique} unique visitors`, icon: Users },
  ];

  const maxDaily = Math.max(1, ...data.daily.map((d) => d.visits));

  return (
    <div data-testid="visitors-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl border border-orange-100 p-4" data-testid={`visitors-card-${c.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500 font-semibold">
              <c.icon className="w-3.5 h-3.5 text-coral" /> {c.label}
            </div>
            <div className="font-serif text-3xl font-semibold mt-1">{c.value}</div>
            <div className="text-xs text-stone-500 mt-0.5">{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-orange-100 p-5">
          <h3 className="font-serif font-semibold text-lg">Daily Visits (last 14 days)</h3>
          <div className="flex gap-1.5 mt-4 h-40" data-testid="visitors-daily-chart">
            {data.daily.map((d) => (
              <div key={d.day} className="flex-1 h-full flex flex-col justify-end items-center gap-1 group relative">
                <div className="absolute -top-6 hidden group-hover:block text-[10px] bg-stone-800 text-white px-1.5 py-0.5 rounded whitespace-nowrap z-10">
                  {d.visits} visits · {d.unique} unique
                </div>
                <div
                  className="w-full rounded-t bg-gradient-to-t from-[#ee5a47] to-[#ff9a7a] min-h-[2px] transition-opacity hover:opacity-80"
                  style={{ height: `${Math.round((d.visits / maxDaily) * 88)}%` }}
                />
                <span className="text-[9px] text-stone-400 rotate-0">{d.day.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-orange-100 p-5">
          <h3 className="font-serif font-semibold text-lg">Most Visited Pages</h3>
          <div className="mt-4 space-y-2.5" data-testid="visitors-top-pages">
            {data.topPages.length === 0 && <div className="text-stone-500 text-sm py-6 text-center">No visits recorded yet.</div>}
            {data.topPages.map((p) => {
              const max = data.topPages[0]?.count || 1;
              return (
                <div key={p.path}>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{PAGE_NAMES[p.path] || p.path}</span>
                    <span className="text-stone-500">{p.count}</span>
                  </div>
                  <div className="h-1.5 bg-orange-50 rounded-full mt-1">
                    <div className="h-full rounded-full bg-[#ee5a47]" style={{ width: `${(p.count / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <p className="text-xs text-stone-400 mt-4">Tracking counts every page view on the public site. Unique visitors are identified per browser/device. Admin pages are excluded.</p>
    </div>
  );
};
